import React, { useContext, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useBreakpointValue,
  Tooltip,
  Box,
  Flex,
  Text,
  Input,
  IconButton,
} from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import { FiFile, FiX } from "react-icons/fi";
import FileViewer from "../FileViewerModal/FileViewer";
import CommentDrawer from "./CommentDrawer";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";
import { RiDeleteBin5Line } from "react-icons/ri";
import { DeleteIcon } from "@chakra-ui/icons";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  type: string;
  id?: any;
  handleGet: () => void;
};
function UploadDocumentModal({
  isOpen,
  onClose,
  type,
  id,
  handleGet,
}: ModalProps) {
  const viewerWidth = useBreakpointValue({ lg: true });
  const { toastNotify, activeWorkflow, accountDetails, workFlowData } =
    useContext(AppContext);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [commentModel, setCommentModel] = useState<any>({
    selectedStatus: "approved",
    open: false,
    comment: "",
    isMulti: false,
  });
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isHoveringDelete, setIsHoveringDelete] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null);

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const handleCloseCommentModal = () => {
    setIsCommentOpen(false);
  };

  const resetFunction = () => {
    setCommentModel({
      selectedStatus: "approved",
      open: false,
      comment: "",
      isMulti: false,
    });
    setSelectedFile(null);
  };

  let width = "50vw";
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      let dt = URL.createObjectURL(file);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setSelectedFile({
          name: file.name,
          file: base64String,
          url: dt,
          type: file.type,
        });
      };
      reader.onerror = (error) => {
        console.error("Error reading file:", error);
      };
    } else {
      alert("Please upload a file");
    }
  };

  const UploadFile = async () => {
    try {
      setLoading(true);
      if (selectedFile) {
        const approval = [
          {
            comment: commentModel.comment,
            type: "document",
            status: "approved",
            name: accountDetails.name,
            level: accountDetails.level,
          },
        ];
        const dt = {
          values: {},
          file: selectedFile,
          tags: {
            extraction: {
              type: "automatic",
            },
          },
          activeWorkflow: activeWorkflow,
          type: "table",
          approval: workFlowData?.differentiateFirstLevel ? approval : [],
          status: "approved",
        };
        const response: any = await appRequest("document", "create", dt);
        if (response.status === "success") {
          toastNotify({
            title: `Document uploaded successfully`,
            message: response.message || "Something went wrong.",
            type: "success",
          });
          resetFunction();
          onClose();
        } else {
          toastNotify({
            title: `Error uploading the document.`,
            message: response.message || "Something went wrong.",
            type: "error",
          });
        }
      }
    } catch (err: any) {
      toastNotify({
        title: `Error getting the document.`,
        message: err.message || "Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMultipleData = async () => {
    try {
      try {
        setLoading(true);
        const updatedStatus = {
          id: id,
          level: accountDetails.level,
          status: commentModel.selectedStatus,
          comment: commentModel.comment,
          values: {},
          type: "document",
          activeWorkflow: activeWorkflow,
          fileId: selectedFile,
          createType: type,
        };

        const response: any = await appRequest(
          "document",
          "update",
          updatedStatus
        );
        if (response.status !== "success") {
          throw new Error(response?.message || `Failed to update document for row`);
        }
      } catch (error: any) {
        return {
          title: "Error",
          message: error.message,
          type: "error",
        };
      }
      toastNotify({
        type: "success",
        title: "Updates submitted",
        message: "Updated successfully.",
      });
      resetFunction();
      onClose();
      handleGet();
    } catch (err) {
      console.error(err);
      toastNotify({
        title: "Error",
        message: "Failed to update document.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  // console.log("modal type", type);

  const onCloseupload = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={onCloseupload} size="full" isCentered>
        <ModalOverlay />
        <ModalContent
          position="fixed"
          right={isOpen ? "0" : `-${width}`}
          top="0"
          height="100vh"
          width={viewerWidth ? width : "100vw"}
          animation={`${isOpen ? "slideIn" : "slideOut"
            } 0.8s ease-in-out forwards`}
        >
          <ModalHeader
            fontSize={{ base: "md", lg: "lg" }}
            color="teal.300"
            fontWeight="700"
            p=".6rem"
            ml="1rem"
            textTransform="capitalize"
          >
            {type === "alternative" ? "Reupload" : type} Document
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              name="document"
              type="file"
              className="chooseFile"
              ref={fileInputRef}
              accept={[".pdf", ".png", ".jpg", ".jpeg", ".webp"].join(",")}
              onChange={(e: any) => handleFileChange(e)}
            />
            {selectedFile && (
              <Box
                mt={5}
                cursor="pointer"
                bg="teal.100"
                borderRadius={4}
                p={2}
                onClick={() => setIsDrawerOpen(true)}
              >
                <Tooltip hasArrow label="View Document" arrowSize={15} isDisabled={isHoveringDelete}>
                  <Flex alignItems="center" justifyContent="space-between" gap={2}>
                    <Flex alignItems="center" gap={4}>
                      <Box color="teal.600">
                        <FiFile size={20} color="teal.500" />
                      </Box>
                      <Box
                        fontSize="lg"
                        p={{ base: 1, md: 2 }}
                        fontWeight="medium"
                        textDecoration="underline"
                        color="blue.500"
                      >
                        <Text
                          isTruncated
                          w={{ base: "12rem", md: "15rem" }}
                          fontSize="sm"
                        >
                          {selectedFile?.name}
                        </Text>
                      </Box>
                    </Flex>
                    <Tooltip label="Delete" hasArrow arrowSize={15}>
                      <Button
                        onMouseEnter={() => setIsHoveringDelete(true)}
                        onMouseLeave={() => setIsHoveringDelete(false)}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          handleDeleteFile()
                        }

                        }
                        cursor="pointer"
                        color="red.500"
                        bg="transparent"
                        _hover={{ color: "red.600", borderColor: "red.600" }}
                        borderColor="red.500"
                        fontSize="xs"
                      >
                        <DeleteIcon fontSize="md" />
                      </Button>
                    </Tooltip>
                  </Flex>
                </Tooltip>
              </Box>
            )}
            <Modal
              isOpen={isDrawerOpen}
              onClose={closeDrawer}
              size="full"
              isCentered
            >
              <ModalOverlay />
              <ModalContent
                position="fixed"
                right={isOpen ? "0" : `-${width}`}
                top="0"
                height="100vh"
                width={viewerWidth ? width : "100vw"}
                animation={`${isOpen ? "slideIn" : "slideOut"
                  } 0.8s ease-in-out forwards`}
              >
                <ModalHeader>
                  <Text fontSize="md" isTruncated w="15rem">
                    {selectedFile?.name}
                  </Text>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody p={2}>
                  {selectedFile && <FileViewer sample={selectedFile} />}
                </ModalBody>
              </ModalContent>
            </Modal>
          </ModalBody>

          <ModalFooter>
            <Flex alignItems="center" gap={2} justifyItems="end">
              <Button
                isDisabled={selectedFile === null}
                colorScheme="green"
                onClick={() => {
                  setCommentModel({
                    open: true,
                    selectedStatus: "approved",
                    comment: "",
                    isMulti: false,
                  });
                }}
              >
                Upload
              </Button>
              <Button colorScheme="red" onClick={onCloseupload}>
                Cancel
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
        <style>
          {`
          @keyframes slideIn {
            from {
              right: -${width};
            }
            to {
              right: 0;
            }
          }
          @keyframes slideOut {
            from {
              right: 0;
            }
            to {
              right: -${width};
            }
          }
          `}
        </style>
        <CommentDrawer
          commentModel={commentModel}
          setCommentModel={setCommentModel}
          handleGet={handleGet}
          isOpen={commentModel.open}
          width={width}
          onClose={handleCloseCommentModal}
          handleClose={onClose}
          uploadFile={type === "upload" ? UploadFile : handleSubmitMultipleData}
          loading={loading}
        />
      </Modal>
    </>
  );
}

export default UploadDocumentModal;
